import type {ReactiveController, ReactiveControllerHost} from 'lit';
import {
  characters,
  generateAyogavaha,
  generateConjuncts,
  generateDependentVowels,
  generateVirama,
  isOpaqueConjunct,
  shuffleArray,
} from './characters';
import type {Character, Option} from './types';
import {
  classify,
  effBox,
  fastThreshold,
  labelFor,
  localDay,
  weightFor,
  type Label,
} from './learner-model';
import {weightedPick, weightedShuffle} from './weighted-random';
import {
  detectStorage,
  ProgressStore,
  SETTING_KEYS,
  type Goal,
  type SettingKey,
  type StorageLike,
} from './progress-store';

export type CategoryKey =
  | 'vowelsChecked'
  | 'consonantsChecked'
  | 'dependentVowelsChecked'
  | 'viramaChecked'
  | 'ayogavahaChecked'
  | 'conjunctsChecked'
  | 'numeralsChecked';

/**
 * 外から差し替えられる依存。テストは Node（localStorage 無し）で走り、時刻と乱数を
 * 固定したいので、ここ以外で Date.now / Math.random / localStorage を直接参照しない。
 * 省略時は実環境の既定（Node では storage が null になりメモリのみで動く）。
 */
export interface QuizDeps {
  storage?: StorageLike | null;
  now?: () => number;
  today?: () => number;
  random?: () => number;
}

export interface CategoryInfo {
  key: CategoryKey;
  label: string;
  en: string;
}

/** 文字種の一覧。並びは学習の順序でもあり、次の文字種を勧めるときはこの順で最初の未選択を出す */
export const CATEGORIES: readonly CategoryInfo[] = [
  {key: 'vowelsChecked', label: '母音', en: 'Vowels'},
  {key: 'consonantsChecked', label: '子音', en: 'Consonants'},
  {key: 'dependentVowelsChecked', label: '依存母音記号', en: 'Dependent Vowels'},
  {key: 'viramaChecked', label: 'ヴィラーマ', en: 'Virāma'},
  {key: 'ayogavahaChecked', label: 'アヌスヴァーラ・ヴィサルガ', en: 'Anusvāra / Visarga'},
  {key: 'conjunctsChecked', label: '結合子音', en: 'Conjuncts'},
  {key: 'numeralsChecked', label: '数字', en: 'Numerals'},
];

/** 保存する文字種 id（progress-store）と、このクラスのフラグ名の対応 */
const categoryKeyOf = (k: SettingKey): CategoryKey => `${k}Checked`;

/** 正解の瞬間に 1 つだけ出す節目。優先順はこの型の宣言順 */
export type MilestoneKind = '定着' | '克服' | '目標達成';
export interface Milestone {
  kind: MilestoneKind;
  char: string;
}

/**
 * 今日の目標に届いたときに、次の問題の代わりに 1 回だけ出す区切り。
 * 数字は出した瞬間の値で固定する（続けたあとに書き換わらない）
 */
export interface GoalCard {
  goal: Goal;
  /** 今日の回答数と正解数 */
  today: number;
  correct: number;
  /** 選択中の文字種のうち今日覚えた字（ほぼ定着で学習日がまだ今日だけ）の字数。日をあけてもう一度正解すると定着になる */
  almost: number;
  /** 出した暦日。日をまたいだら数字が「今日」でなくなるので畳む */
  day: number;
}

export interface ProgressSummary {
  /** 今日の回答数と目標 */
  today: number;
  goal: Goal;
  /** 選択中の文字種のうち「定着」の字数 */
  mastered: number;
  /** 選択中の文字種のうち実効箱 3 以上（学習中〜定着）の字数。定着を含む */
  advanced: number;
  /**
   * 定着バーと `定着 N / M` の分母。選択中の文字種のうち**出題済み（記録のある）の字数**で、
   * 最低でも今の出題セットの字数。選択中の合計にすると、依存母音記号を足した瞬間に
   * 14 → 344 に跳ねて貯めた定着バーが消え、最も前に進んだ学習者を罰する
   */
  total: number;
}

export interface CategoryBreakdown extends CategoryInfo {
  mastered: number;
  learning: number;
  ambiguous: number;
  unseen: number;
  total: number;
}

/** 結果表示 1000ms → 次問まで 300ms（合計 1.3 秒）*/
const RESULT_MS = 1000;
const ADVANCE_MS = 300;

/** 不正解の直後、その字を据え置く問数。間違えた直後に叩き返さない */
const HOLD_QUESTIONS = 3;
/** 据え置きが明けたあとの復帰の重み。その字に次に正解した時点で解除 */
const WRONG_BOOST = 2.0;
/** 直近この問数がすべて「苦手」なら、次は苦手以外から出す（息継ぎ） */
const RELIEF_WINDOW = 4;
/** 文字種の誘導を出す条件: そのセッションの回答数と、ほぼ定着以上の割合 */
const SUGGEST_MIN_ANSWERS = 20;
const SUGGEST_SHARE = 0.6;
/**
 * 目標の区切りを出すのに、そのセッションで最低限答えている問数。前回 19 問で終えた人が
 * 再訪して 1 問答えた直後に「今日はここまで」と言われないため。区切りの直後に提案を
 * 見合わせる問数も同じ値
 */
const GOAL_CARD_MIN_ANSWERS = 5;
/**
 * はじめて出る字（記録が無い字）に読みを添えておく時間。8 字未見 × 3 択では初心者の最初の
 * 1 分は約 2/3 が誤答で、そのたびに説明パネルと「次へ」が挟まる。最初の 1 回だけ
 * 「字 = 読み」を見せて、当てずっぽうではなく対応づけから始めさせる
 */
export const PREVIEW_MS = 3_500;
/**
 * 読みから字を選ぶ向き（反転）で出す下限の実効箱。箱 4 は最低 3 回の回答を要するので
 * 「別の機会にも読めた字」に限られる。箱 3 だと初日の速答 2 回で届き、2 回しか見ていない字が回る
 */
const REVERSE_MIN_BOX = 4;
/** 消去の確認が自動で閉じるまで／取り消しができる猶予 */
const CONFIRM_MS = 8_000;
const UNDO_MS = 10_000;
/** 連続正解チップを出し始める回数と、表示上の上限 */
export const STREAK_MIN = 3;
export const STREAK_CAP = 20;
/** 「まだ曖昧な字」の一覧の件数 */
const WEAK_LIST = 10;

/** 直近 R 問を候補から外す。プールが小さいときに候補が 1 件に潰れないよう段階制にする */
function ringSize(poolSize: number): number {
  const r = poolSize <= 4 ? 1 : poolSize <= 8 ? 2 : 3;
  return Math.min(r, poolSize - 1);
}

export class QuizController implements ReactiveController {
  // 文字種の選択状態
  vowelsChecked = true;
  consonantsChecked = false;
  dependentVowelsChecked = false;
  viramaChecked = false;
  ayogavahaChecked = false;
  conjunctsChecked = false;
  numeralsChecked = false;

  /** 選択中の文字種の合計。何も選ばれていなければ 0 */
  maxCharacters = characters.vowels.length;

  // 出題プールと現在の問題
  selectedCharacters: Character[] = [];
  currentCharacter: Character | null = null;
  options: Option[] = [];
  showResult = false;
  resultCorrect: boolean | null = null;
  /**
   * 不正解のあと「次へ」待ち。正解は従来どおり 1.3 秒で自動送りするが、
   * 不正解のときは対比の説明を読む時間を確保するため自動では進めない。
   */
  awaitingContinue = false;
  /** 直前に選ばれた選択肢。不正解のとき「選んだ字」と「正解の字」を並べて出すのに使う */
  lastChoice: Character | null = null;

  /**
   * 使用方法パネルの開閉。クイズの状態とは無関係。
   *
   * 既定は閉じている。展開したままだと実高が幅によって 1646〜2625px まで振れ、
   * 埋め込み側の FOUC プレースホルダを 1 組の値で覆えない（高さは幅に対して単調でないので、
   * 段を増やしても取りこぼす）。閉じておけば振れ幅が 180px 程度に収まる。
   * 副次的に、375px 幅で出題ボックスの上端が 1875px から 579px まで上がり、
   * スマホで 2 画面ぶんスクロールしないとクイズに届かない状態が解消する。
   */
  instructionsExpanded = false;

  /** 学習の記録パネル（既定で閉。開いたときだけ描画する） */
  progressPanelOpen = false;
  /** 記録の消去の 2 段目（確認中） */
  confirmingClear = false;
  /** 消去したあと「元に戻す」を出している間、消した件数を持つ。null なら出していない */
  clearedCount: number | null = null;
  /** 直前の消去がストレージにも及んだか。保存できない環境ではメモリだけが消える */
  clearedPersisted = true;
  /** 正解の自動送りの代わりに出す、次の文字種の提案 */
  pendingSuggestion: CategoryInfo | null = null;
  /** 直前の正解で立った節目。○ を出している間だけ意味を持つ */
  milestone: Milestone | null = null;
  /** 連続正解。不正解で 0 に戻る。リセット（プールの引き直し）では戻さない */
  streak = 0;
  /**
   * 今日の目標に届いた区切り。次の問題の代わりに選択肢のスロットへ出す（提案と同じ場所）。
   * 「続ける」で次の問題、「今日はここまで」で sessionClosed へ
   */
  goalCard: GoalCard | null = null;
  /**
   * 「今日はここまで」を選んだあとの休止状態。出題ボックスはまとめの文だけを出し、
   * 選択肢の代わりに「続ける」だけを置く。設定の変更・文字の入れ替え・続ける で解除
   */
  sessionClosed = false;
  /** 休止中に出題ボックスへ出すまとめ（カードの数字を引き継ぐ）。sessionClosed のときだけ意味を持つ */
  closedSummary: GoalCard | null = null;
  /**
   * はじめて出る字の教示。出題直後 PREVIEW_MS のあいだ「字 = 読み」を添える。
   * 選択肢は普段どおり押せる（ロックしない。初期描画も変えない）。
   * 分類は通常どおり（箱 0 からは fast も untimed も 2 で、slow だけが「待って写した」を
   * 箱 1 に留める正しい値付けになる）。反応時間だけは個人適応の窓に入れない
   */
  preview: {char: string; romanized: string} | null = null;
  /** 今の問題が記録の無い字（教示の対象）。添えが消えたあとも、その問題のあいだ真 */
  firstContact = false;
  /**
   * 読み方から字を選ぶ向き。実効箱 4 以上の字に限り、その字の出題を交互に反転する
   * （回答数の偶奇。乱数にしないのはテストと体感を決定的にするため）。
   * 選択肢の中身（#generateOptions）は同じで、どちらを提示側にするかだけが変わる
   */
  reversed = false;
  /**
   * 反転を混ぜるか（学習の記録パネルで切れる。音声で使う人・片方向だけ練習したい人向け）。
   * 文字種と同じく設定として保存し、次回も引き継ぐ
   */
  reverseEnabled = true;

  #host: ReactiveControllerHost;
  #now: () => number;
  #today: () => number;
  #random: () => number;
  #store: ProgressStore;
  /** 直近に出した問題の id（新しいものが末尾）。R 問の連投禁止に使う */
  #recent: string[] = [];
  /** 直近に出した問題の実効箱（0 = 未学習）。息継ぎの判定に使う */
  #recentBoxes: number[] = [];
  /** 不正解の直後に据え置く残り問数 */
  #hold = new Map<string, number>();
  /** 据え置き明けの復帰の重み */
  #boost = new Map<string, number>();
  #questionShownAt = 0;
  /** 今の問題は反応時間を計測しない（セッション最初の 1 問・フォント未ロード・タブ切替） */
  #questionUntimed = true;
  #timingBlocked = false;
  #anyServed = false;
  #answeredThisSession = 0;
  #suggestionDone = false;
  /**
   * 目標に届いた回答のあと、次の問題に進む地点で区切りに差し替える。
   * 届く回答は 1 日に 1 回しか立たないので、文字の入れ替えや切断で捨てない
   */
  #goalPending = false;
  /** 保留を立てた暦日。日をまたいだ保留は「今日」の区切りではないので捨てる */
  #goalPendingDay = 0;
  /** 区切りを出した暦日。1 セッション・かつ 1 暦日に 1 回（日をまたぐ長いセッションでは翌日ぶんが出る） */
  #goalCardDay: number | null = null;
  /** 区切りの直後は提案を出さない（割り込みカードを連投しない）。この回答数まで提案を見合わせる */
  #suggestBlockUntil = 0;
  #previewTimer?: ReturnType<typeof setTimeout>;
  /** 添えが出ている間に答えた（反応時間を個人適応の窓に入れない） */
  #previewAssisted = false;
  /** 今の問題が反転で出題されたか。切り替えで表示を戻しても採点はこちらを見る（答えが露出している） */
  #servedReversed = false;
  #confirmTimer?: ReturnType<typeof setTimeout>;
  #undoTimer?: ReturnType<typeof setTimeout>;
  #allCache: Character[] | null = null;
  #previousCharacter: Character | null = null;
  #dependentVowelsCache: Character[] | null = null;
  #viramaCache: Character[] | null = null;
  #ayogavahaCache: Character[] | null = null;
  #conjunctsCache: Character[] | null = null;
  #hideTimer?: ReturnType<typeof setTimeout>;
  #nextTimer?: ReturnType<typeof setTimeout>;
  /** 回答から次問までの 1.3 秒間、二重回答を止める */
  #answerLocked = false;
  #started = false;

  /**
   * ユーザーが意図した文字数。実際に使う characterCount はこれを上限でクランプしたもの。
   * 生の値を保持しておくことで、文字種を減らして戻したときに元の値が復元される
   * （旧実装は片道クランプで、母音14 → 母音を外す → 戻す、で 2 に潰れたまま戻らなかった）。
   *
   * 既定は 8。React 版から引き継いだ 2 だと、直近の字を避ける規則で 2 字が交互に出るだけになり、
   * 学習の記録に基づく出題の調整が原理的に何も起こさない（初日の実測: 未知/既知の出題比が
   * プール 2 で 1.00、4 で 1.1〜1.2、8 で 1.9〜2.5。既知の字が箱 4 に留まる初日の値で、
   * 2 日目は 8 で 2.1〜3.0）。
   */
  #desiredCount = 8;

  constructor(host: ReactiveControllerHost, deps: QuizDeps = {}) {
    this.#host = host;
    this.#now = deps.now ?? (() => Date.now());
    this.#today = deps.today ?? (() => localDay(this.#now()));
    this.#random = deps.random ?? Math.random;
    this.#store = new ProgressStore({
      storage: deps.storage === undefined ? detectStorage() : deps.storage,
      today: this.#today,
    });
    this.#restoreSettings();
    host.addController(this);
  }

  /**
   * 前回の文字種と文字数で始める。hostConnected の #recomputeMax → resetQuiz より前に
   * フラグを立てるので、最初のプールから前回の設定で引かれる。
   * 文字数は生の値を戻し、上限のクランプは characterCount に任せる
   */
  #restoreSettings() {
    const saved = this.#store.settings;
    if (!saved) return;
    if (saved.sel) for (const k of SETTING_KEYS) this[categoryKeyOf(k)] = saved.sel.includes(k);
    if (saved.cnt !== undefined) this.#desiredCount = saved.cnt;
    if (saved.rev === false) this.reverseEnabled = false;
  }

  /** 文字種・文字数・反転の現在値を保存する（文字種の変更・スライダーを離した時点・提案の受け入れ・反転の切り替え） */
  #saveSettings() {
    this.#store.setSettings(
      SETTING_KEYS.filter((k) => this[categoryKeyOf(k)]),
      this.#desiredCount,
      this.reverseEnabled
    );
  }

  /** 文字種が 1 つも選ばれていない状態 */
  get hasSelection(): boolean {
    return this.maxCharacters > 0;
  }

  /** スライダーの上限。文字種未選択でもコントロールを壊さないよう最低 2 */
  get sliderMax(): number {
    return Math.max(2, this.maxCharacters);
  }

  /** 実際に使う出題文字数 */
  get characterCount(): number {
    return Math.min(Math.max(2, this.#desiredCount), this.sliderMax);
  }

  /** 回答直後で入力を受け付けない状態 */
  get locked(): boolean {
    return this.#answerLocked;
  }

  hostConnected() {
    if (this.#started) return;
    this.#started = true;
    this.#recomputeMax();
    this.resetQuiz();
  }

  hostDisconnected() {
    // 保留中のタイマーがあるまま切断されると、○/× を出したまま次に進めなくなる。
    // 切断時点で表示を確定させ、再接続後に固まらないようにする。
    const wasAdvancing =
      this.#hideTimer !== undefined || this.#nextTimer !== undefined || this.awaitingContinue;
    // 区切りのカードも提案と同じで、切断されたら消して次の問題を用意しておく。
    // ただし押されていないカードは保留に戻す（その日の区切りを切断で失わない）。
    // 「今日はここまで」の休止は利用者の意思なので、そのまま残す。
    // 教示の添えは #clearTimers が消す（再接続後にタイマーだけ生き残らない）
    const wasSuggesting = this.pendingSuggestion !== null || this.goalCard !== null;
    if (this.goalCard) {
      this.#goalPending = true;
      this.#goalPendingDay = this.goalCard.day;
      this.#goalCardDay = null;
    }
    this.#clearTimers();
    clearTimeout(this.#confirmTimer);
    clearTimeout(this.#undoTimer);
    this.confirmingClear = false;
    if (this.clearedCount !== null) {
      this.clearedCount = null;
      this.#store.forgetUndo();
    }
    if (wasAdvancing || wasSuggesting) {
      this.showResult = false;
      this.resultCorrect = null;
      this.awaitingContinue = false;
      this.pendingSuggestion = null;
      this.goalCard = null;
      this.milestone = null;
      this.#nextQuestion();
    }
    // 切断中にタイマーを残さない。再接続後の問題に添えは付かない（計測は通常どおり）
    this.#endPreview();
    // 切断は「ページを離れる」の最後の合図になりうるので、保留中の記録を今書く
    this.#store.flush();
  }

  #clearTimers() {
    clearTimeout(this.#hideTimer);
    clearTimeout(this.#nextTimer);
    this.#hideTimer = undefined;
    this.#nextTimer = undefined;
    this.#answerLocked = false;
    this.#endPreview();
  }

  #endPreview() {
    clearTimeout(this.#previewTimer);
    this.#previewTimer = undefined;
    this.preview = null;
  }

  /** 子音 × 母音記号の全組み合わせ。初回アクセス時に一度だけ生成する */
  get #dependentVowels(): Character[] {
    this.#dependentVowelsCache ??= generateDependentVowels();
    return this.#dependentVowelsCache;
  }

  get #virama(): Character[] {
    this.#viramaCache ??= generateVirama();
    return this.#viramaCache;
  }

  get #ayogavaha(): Character[] {
    this.#ayogavahaCache ??= generateAyogavaha();
    return this.#ayogavahaCache;
  }

  get #conjuncts(): Character[] {
    this.#conjunctsCache ??= generateConjuncts();
    return this.#conjunctsCache;
  }

  /**
   * 現在の出題文字を組み立てている素の子音字。
   * 不正解のときに「क = ka / क् = k」の対比を出すために使う。
   *
   * 結合子音は基底が 2 つあるので複数返る。ただし क्ष / ज्ञ は構成要素の形が字に
   * 残らないので、分解を見せると画面に無いものを指すことになる（Whitney §13）。
   * その 2 件は空を返し、note の「この形ごと覚える」だけを出す。
   *
   * 参照元は characters.consonants なので、その文字種が選ばれていなくても対比は出る。
   */
  get contrastBases(): Character[] {
    const current = this.currentCharacter;
    if (!current) return [];
    if (isOpaqueConjunct(current.char)) return [];
    const keys = current.components ?? (current.pairKey ? [current.pairKey] : []);
    return keys
      .map((k) => characters.consonants.find((c) => c.char === k))
      .filter((c): c is Character => !!c && c.id !== current.id);
  }

  /** 後方互換。呼び出し元を一度に書き換えないための入口 */
  get contrastBase(): Character | null {
    return this.contrastBases[0] ?? null;
  }

  #recomputeMax() {
    let max = 0;
    if (this.vowelsChecked) max += characters.vowels.length;
    if (this.consonantsChecked) max += characters.consonants.length;
    if (this.dependentVowelsChecked) max += this.#dependentVowels.length;
    if (this.viramaChecked) max += this.#virama.length;
    if (this.ayogavahaChecked) max += this.#ayogavaha.length;
    if (this.conjunctsChecked) max += this.#conjuncts.length;
    if (this.numeralsChecked) max += characters.numerals.length;
    this.maxCharacters = max;
    this.#host.requestUpdate();
  }

  #allSelected(): Character[] {
    let available: Character[] = [];
    if (this.vowelsChecked) available = available.concat(characters.vowels);
    if (this.consonantsChecked) available = available.concat(characters.consonants);
    if (this.dependentVowelsChecked) available = available.concat(this.#dependentVowels);
    if (this.viramaChecked) available = available.concat(this.#virama);
    if (this.ayogavahaChecked) available = available.concat(this.#ayogavaha);
    if (this.conjunctsChecked) available = available.concat(this.#conjuncts);
    if (this.numeralsChecked) available = available.concat(characters.numerals);
    return available;
  }

  /**
   * 出題プールを引く。
   *
   * 単純にシャッフルして切ると、क と क् が同じプールに入る確率は実測で 3〜5% しかなく、
   * 内在母音の対比が事実上一度も提示されない。文字を選ぶたびに、同じアクシャラを基にした
   * 別形（का / क् / कं …）を最大 2 件まで道連れにして、対比の相手をプール内に確保する。
   * プールの件数は characterCount のままなので、スライダーの意味は変わらない。
   */
  #getAvailableCharacters(): Character[] {
    const available = this.#allSelected();
    const count = this.characterCount;
    if (available.length <= count) return shuffleArray(available, this.#random);

    const siblings = new Map<string, Character[]>();
    for (const c of available) {
      if (!c.pairKey) continue;
      const list = siblings.get(c.pairKey);
      if (list) list.push(c);
      else siblings.set(c.pairKey, [c]);
    }

    const pool: Character[] = [];
    const taken = new Set<string>();

    for (const seed of this.#seedOrder(available)) {
      if (pool.length >= count) break;
      if (taken.has(seed.id)) continue;
      pool.push(seed);
      taken.add(seed.id);

      if (!seed.pairKey) continue;
      let added = 0;
      for (const sibling of shuffleArray(siblings.get(seed.pairKey) ?? [], this.#random)) {
        if (pool.length >= count || added >= 2) break;
        if (taken.has(sibling.id)) continue;
        pool.push(sibling);
        taken.add(sibling.id);
        added++;
      }
    }

    return shuffleArray(pool, this.#random);
  }

  /**
   * プールの種の順。記録があれば苦手な字が先に来やすくする。
   *
   * 重みは問題選択のもの（苦手 6.55 : 定着 1.00）をそのまま使わず、√ を取って平均で
   * 正規化し 0.25 + 0.75 × に潰す。比は履歴によらず 1.84〜2.17 に収まる。これより強くすると
   * 「素の子音字だけ苦手」の履歴で、7 文字種 30 件のプールに素の子音字が 3 件を超えて入り、
   * 依存母音記号の誤答が「マートラーの弁別」から「マートラーの有無」に変わる（実測）。
   *
   * **道連れ（siblings）の shuffleArray には重みを付けない。** 定着した क を「もう要らない」と
   * 外した瞬間、曖昧な क् の対比相手が消える。対比は学習者の状態ではなく教材の性質に属する。
   * 出題比が物足りなくても、触ってよいのは問題選択側の重みだけ。
   */
  #seedOrder(available: Character[]): Character[] {
    if (this.#store.isEmpty) return shuffleArray(available, this.#random); // 現行と同一（本番の乱数源は Math.random）
    const today = this.#today();
    const roots = available.map((c) => Math.sqrt(weightFor(this.#store.rec(c.id), today)));
    const mean = roots.reduce((s, x) => s + x, 0) / roots.length;
    const pw = new Map(available.map((c, i) => [c.id, 0.25 + (0.75 * roots[i]) / mean]));
    return weightedShuffle(available, (c) => pw.get(c.id) ?? 1, this.#random);
  }

  /**
   * 選択肢は正解 + 同じプールからの誤答最大 2 個。
   * ラベルが重複しないよう、除外はローマ字表記で行う（表示上の同一性）。
   */
  #generateOptions(correct: Character): Option[] {
    const available = this.selectedCharacters.filter(
      (char) => char.romanized !== correct.romanized
    );
    if (correct.components) return this.#conjunctOptions(correct, available);
    if (available.length === 0) return [correct];

    // 同じアクシャラの別形を先に取る。誤答 2 枠とも同じ基底文字にできれば
    // 「ka / k / kā」の実質 3 択になり、当てずっぽうの下限が 50% から 33% に下がる
    const sameBase = correct.pairKey
      ? shuffleArray(available.filter((c) => c.pairKey === correct.pairKey))
      : [];
    const sameBaseIds = new Set(sameBase.map((c) => c.id));
    const others = shuffleArray(available.filter((c) => !sameBaseIds.has(c.id)));

    const wrongOptions = [...sameBase, ...others].slice(0, Math.min(2, available.length));
    return shuffleArray([correct, ...wrongOptions]);
  }

  /**
   * 結合子音の誤答。
   *
   * プールの引き方（#getAvailableCharacters）には一切触らない。あちらに手を入れて
   * 素の子音字を優先的に道連れにすると、結合子音のチェックを外していても効いてしまい、
   * 依存母音記号の問題が「マートラーの弁別」から「マートラーの有無」に変わる。
   * 誤答の選び方だけで対比を作れば、既存カテゴリの挙動は 1 ビットも変わらない。
   *
   * 誤答 2 枠の内訳:
   *   1 枠目 … 構成要素を 1 つ共有する別の結合子音。両方の要素を読まないと外せない。
   *            プールに無ければ結合子音の全体から引く（contrastBases が未選択カテゴリの
   *            文字を出すのと同じ考え方）。これが無いと、素の子音字だけが誤答になり
   *            「一番長い選択肢を選ぶ」だけで当たるようになる。
   *   2 枠目 … 素の構成子音。「2 字の合成で、まえの字は内在母音 a を失う」を毎問見せる。
   *            क्ष / ज्ञ は構成要素の形が残らないので、代わりに別の結合子音を置く。
   */
  #conjunctOptions(correct: Character, available: Character[]): Option[] {
    const keys = correct.components ?? [];
    const used = new Set([correct.romanized]);
    const take = (candidates: Character[]): Character | null => {
      for (const c of candidates) {
        if (!used.has(c.romanized)) {
          used.add(c.romanized);
          return c;
        }
      }
      return null;
    };
    const wanted = correct.romanized.length;

    const kin = this.#conjuncts.filter(
      (c) => c.id !== correct.id && (c.components ?? []).some((k) => keys.includes(k))
    );
    const inPool = new Set(available.map((c) => c.id));
    const wrong: Character[] = [];

    /**
     * 誤答に使う別の結合子音を 1 件選ぶ。
     *
     * 素の構成子音は必ず正解より短いので、それだけを誤答にすると
     * 「一番長いラベルを選ぶ」だけで当たってしまう。もう 1 枠には
     * 正解と同じ長さ以上の結合子音を置いて、長さを手がかりにできなくする。
     *
     * **長さで絞るのが先、同族かプール内かは後。** 順序を逆にすると、
     * 同族に短いものしか無い字（च्छ = ccha の同族は श्च = śca だけ、
     * ष्ठ = ṣṭha の同族は 3 字のものばかり）で正解が常に唯一の最長ラベルになり、
     * その字だけボットの正答率が 100% になる。
     */
    const tiers = [
      kin.filter((c) => inPool.has(c.id)),
      kin,
      this.#conjuncts.filter((c) => c.id !== correct.id),
    ];
    const pickConjunct = (): Character | null => {
      for (const tier of tiers) {
        const c = take(shuffleArray(tier.filter((x) => x.romanized.length >= wanted)));
        if (c) return c;
      }
      for (const tier of tiers) {
        const c = take(shuffleArray(tier));
        if (c) return c;
      }
      return null;
    };

    const sibling = pickConjunct();
    if (sibling) wrong.push(sibling);

    if (isOpaqueConjunct(correct.char)) {
      const another = pickConjunct() ?? take(shuffleArray(available));
      if (another) wrong.push(another);
    } else {
      const bare = take(
        shuffleArray(
          keys
            .map((k) => characters.consonants.find((c) => c.char === k))
            .filter((c): c is Character => !!c)
        )
      );
      if (bare) wrong.push(bare);
    }

    // 埋まらなかった枠はプールから補う（結合子音が 1 件しか無い等の縮退時）
    while (wrong.length < 2) {
      const filler = take(shuffleArray(available));
      if (!filler) break;
      wrong.push(filler);
    }

    return shuffleArray([correct, ...wrong]);
  }

  #nextQuestion() {
    const pool = this.selectedCharacters;
    if (pool.length === 0) {
      this.currentCharacter = null;
      this.options = [];
      return;
    }

    // 記録が 1 件も無ければ現行の経路そのもの。連続抑制も重み付けも通らない。
    // 既存の統計テストは回答せずに resetQuiz() を繰り返すので、必ずこちらを走る
    const picked = this.#store.isEmpty ? this.#uniformPick(pool) : this.#adaptivePick(pool);

    this.#previousCharacter = picked;
    this.currentCharacter = picked;
    this.options = this.#generateOptions(picked);
    this.#serve(picked);
  }

  /** 連続で同じ文字を出さない。文字そのものの同一性なので id で比較する */
  #uniformPick(pool: Character[]): Character {
    let availableChars = pool.filter((char) =>
      this.#previousCharacter ? char.id !== this.#previousCharacter.id : true
    );
    if (availableChars.length === 0) {
      availableChars = pool;
    }
    return availableChars[Math.floor(this.#random() * availableChars.length)];
  }

  /**
   * 記録に基づく出題。
   *
   * 1. 直近 R 問と、不正解直後の据え置き中の字を候補から外す（R はプールの大きさで 1〜3）。
   *    候補が空なら現行と同じ「直前と同じ id を避けるだけ」に縮退する。
   * 2. 息継ぎ: 直近 4 問がすべて苦手（実効箱 1）なら、次は苦手以外から出す。
   *    3-of-4 にすると適応そのものが死ぬ（未知/既知の出題比が 5.3 → 1.4。実測）。
   * 3. 重み 1.6^(5 − 実効箱) × 据え置き明けの復帰 2.0 で 1 件抽選する。指数は動かさない。
   */
  #adaptivePick(pool: Character[]): Character {
    const today = this.#today();
    const boxOf = (c: Character) => {
      const r = this.#store.rec(c.id);
      return r ? effBox(r, today) : 0;
    };

    const recent = new Set(this.#recent.slice(-ringSize(pool.length)));
    let cand = pool.filter((c) => !recent.has(c.id) && !(this.#hold.get(c.id) ?? 0));
    if (cand.length === 0) {
      cand = pool.filter((c) => c.id !== this.#previousCharacter?.id);
    }
    if (cand.length === 0) cand = pool;

    const last = this.#recentBoxes.slice(-RELIEF_WINDOW);
    if (last.length === RELIEF_WINDOW && last.every((b) => b === 1)) {
      const relief = cand.filter((c) => boxOf(c) !== 1);
      if (relief.length > 0) cand = relief;
    }

    return weightedPick(
      cand,
      (c) => weightFor(this.#store.rec(c.id), today) * (this.#boost.get(c.id) ?? 1),
      this.#random
    );
  }

  /** 出題した瞬間の帳簿付け。据え置きのカウントダウンと反応時間の起点 */
  #serve(c: Character) {
    const r = this.#store.rec(c.id);
    const box = r ? effBox(r, this.#today()) : 0;
    this.#recent.push(c.id);
    this.#recentBoxes.push(box);
    if (this.#recent.length > RELIEF_WINDOW) {
      this.#recent.shift();
      this.#recentBoxes.shift();
    }
    for (const [id, n] of this.#hold) {
      if (n <= 1) this.#hold.delete(id);
      else this.#hold.set(id, n - 1);
    }
    this.#questionShownAt = this.#now();
    // セッション最初の 1 問は必ず計測しない（画面を把握する時間が混ざる）
    const first = !this.#anyServed;
    this.#questionUntimed = this.#timingBlocked || first;
    this.#anyServed = true;

    // 向きは出題する字を決めたあとで決める（どの字を出すかには一切影響しない）
    this.#endPreview();
    this.#previewAssisted = false;
    this.firstContact = !r;
    if (!r) {
      // はじめての字: 読みを添える。添えが消えたら、そこから計測をやり直す
      this.reversed = false;
      this.preview = {char: c.char, romanized: c.romanized};
      this.#previewTimer = setTimeout(() => {
        this.#previewTimer = undefined;
        this.preview = null;
        this.#questionShownAt = this.#now();
        this.#host.requestUpdate();
      }, PREVIEW_MS);
    } else if (this.reverseEnabled && box >= REVERSE_MIN_BOX && this.#answeredThisSession > 0) {
      // その字の回答数の偶奇で向きを決める（字ごとに交互）。全体で 1 つのトグルにすると、
      // 2 字が交互に出るプールで片方が常に反転側に固定され、反転は箱を進めないので
      // その字が永久に箱 5 に届かない。そのセッションで 1 問も答えていない間は必ず通常の向き
      // （#anyServed は計測の話で、引き直しで 1 問目が置き換わると破れる）
      this.reversed = r[2] % 2 === 1;
    } else {
      this.reversed = false;
    }
    this.#servedReversed = this.reversed;
  }

  resetQuiz() {
    this.#clearTimers();

    this.selectedCharacters = this.#getAvailableCharacters();
    this.#previousCharacter = null;
    this.currentCharacter = null;
    this.options = [];
    this.showResult = false;
    this.resultCorrect = null;
    this.awaitingContinue = false;
    this.lastChoice = null;
    this.pendingSuggestion = null;
    this.milestone = null;
    // 区切りと休止は、文字を入れ替える・設定を変える、で解除する（続ける意思の表明）。
    // 保留（#goalPending）は残す。届く回答は 1 日に 1 回しか立たないので、○ の 1.3 秒の
    // 間に設定を触っただけでその日の区切りが永久に失われてはいけない
    this.goalCard = null;
    this.sessionClosed = false;
    this.closedSummary = null;
    // プールが変わるので連投禁止の帳簿だけ捨てる。据え置き・復帰・連続正解は
    // 学習の状態なので残す（リセットはプールの引き直しであって学習の中断ではない）
    this.#recent = [];
    this.#recentBoxes = [];

    this.#nextQuestion();
    this.#host.requestUpdate();
  }

  /**
   * 回答。elapsedMs は出題から回答までの時間で、省略すると注入された時計から計算する
   * （テストが反応時間を作るための入口。undefined を明示的に渡すことはできないので、
   * 計測不能にしたいときは markUntimed() を使う）。
   */
  checkAnswer(selected: string, elapsedMs?: number) {
    // 結果表示中〜次問までの 1.3 秒は受け付けない。
    // 旧実装はここが無く、1 問に何度でも回答できて ○ を出せてしまった。
    if (!this.currentCharacter || this.#answerLocked) return;

    this.#answerLocked = true;
    // 教示の途中で答えたら添えを消す。その回答の反応時間は個人適応の窓に入れない
    this.#previewAssisted = this.preview !== null;
    this.#endPreview();
    this.lastChoice = this.options.find((o) => o.romanized === selected) ?? null;
    const correct = selected === this.currentCharacter.romanized;
    this.resultCorrect = correct;
    this.showResult = true;
    this.milestone = this.#record(this.currentCharacter, correct, elapsedMs);

    if (!correct) {
      // 対比の説明を読む時間を確保するため、不正解では自動送りしない
      this.awaitingContinue = true;
      this.#host.requestUpdate();
      return;
    }

    this.#host.requestUpdate();

    this.#hideTimer = setTimeout(() => {
      this.#hideTimer = undefined;
      this.showResult = false;
      this.resultCorrect = null;
      this.#host.requestUpdate();

      this.#nextTimer = setTimeout(() => {
        this.#nextTimer = undefined;
        this.milestone = null;
        // 目標の区切りは提案より先。提案はその後の正解で改めて判定される（#suggestionDone は立てない）
        if (this.#takeGoalCard()) {
          this.#host.requestUpdate();
          return;
        }
        const next =
          this.#answeredThisSession >= this.#suggestBlockUntil ? this.#nextSuggestion() : null;
        if (next) {
          // 次の問題の代わりに提案を出す。答えるまでロックは解かない。
          // 「1 セッション 1 回」は出した瞬間に確定させる。accept / dismiss で立てると、
          // リセット・文字種の変更・スライダー・切断で提案が消えたときに立たず、
          // 以後正解のたびに選択肢が提案に差し替わる（実測 60 問中 23 回）
          this.#suggestionDone = true;
          this.#clearOrientation();
          this.pendingSuggestion = next;
        } else {
          this.#answerLocked = false;
          this.#nextQuestion();
        }
        this.#host.requestUpdate();
      }, ADVANCE_MS);
    }, RESULT_MS);
  }

  /** 回答を記録して、正解なら節目を返す */
  #record(c: Character, correct: boolean, elapsedMs: number | undefined): Milestone | null {
    const rt =
      elapsedMs !== undefined
        ? elapsedMs
        : this.#questionUntimed
          ? undefined
          : this.#now() - this.#questionShownAt;
    const outcome = classify(correct, rt, fastThreshold(this.#store.rts, this.options.length));
    // 反応時間の個人適応の窓には、通常向き・教示なしの正解だけを入れる。
    // 反転（字を 3 つ見比べる）は構造的に長く、教示つき（写すだけ）は構造的に短いので、
    // どちらも中央値を動かして「読むのが遅い成人を箱 2 で頭打ちにしない」装置を狂わせる。
    // 反転は箱も動かさない（閾値が通常向きの式なので、箱 5 の正解が slow で 4 に落ちて
    // 定着バーが縮む）。回答数・誤答数・学習日・据え置き・連続正解は通常どおり。
    // 見るのは出題時の向き（#servedReversed）。反転の切り替えで表示を通常向きに戻した問題は
    // 読みを問題文として見せたあとで答えが露出しているので、正解で記録を動かさず誤答で箱を落とさない
    const {before, after, goalReached} = this.#store.recordAnswer(
      c.id,
      outcome,
      correct && !this.#previewAssisted && !this.#servedReversed ? rt : undefined,
      {scoreBox: !this.#servedReversed}
    );
    this.#answeredThisSession++;
    // 不正解で届いた場合も、「次へ」のあとに区切りを出す
    if (goalReached && this.#goalCardDay !== this.#today()) {
      this.#goalPending = true;
      this.#goalPendingDay = this.#today();
    }

    if (!correct) {
      this.streak = 0;
      this.#hold.set(c.id, HOLD_QUESTIONS);
      this.#boost.set(c.id, WRONG_BOOST);
      return null;
    }

    this.streak++;
    this.#boost.delete(c.id);

    const today = this.#today();
    const was = labelFor(before, today);
    const now = labelFor(after, today);
    if (now === '定着' && was !== '定着') return {kind: '定着', char: c.char};
    // 「克服」は箱 2 → 3 で、かつ一度は間違えている字。初日から出せる唯一の達成
    if (before && before[0] === 2 && after[0] === 3 && after[3] >= 1) {
      return {kind: '克服', char: c.char};
    }
    if (goalReached) return {kind: '目標達成', char: c.char};
    return null;
  }

  /**
   * 次の文字種を勧めるか。そのセッションで 20 問以上答え、選択中の文字種の 6 割が
   * ほぼ定着以上で、まだ選んでいない文字種があるとき、1 セッションに 1 回だけ。
   * 「定着」を条件にすると別の暦日が要るので初日には絶対に出ない。ここだけ実効箱 4 以上で見る
   */
  #nextSuggestion(): CategoryInfo | null {
    if (this.#suggestionDone || this.#answeredThisSession < SUGGEST_MIN_ANSWERS) return null;
    const next = CATEGORIES.find((c) => !this[c.key]);
    if (!next) return null;
    const today = this.#today();
    // 分母は出題済み（記録のある）字。選択中の合計にすると、既定の 8 / 14 では出題される
    // 8 字しか記録を持てず、割合の上限が 0.57 < 0.6 で永久に出なかった。
    // 出題セット（重みで弱い字に寄る）を分母にすると、引き直したあとは逆に出にくくなる
    let encountered = 0;
    let advanced = 0;
    for (const c of this.#allSelected()) {
      const r = this.#store.rec(c.id);
      if (!r) continue;
      encountered++;
      if (effBox(r, today) >= 4) advanced++;
    }
    if (encountered === 0) return null;
    return advanced / encountered >= SUGGEST_SHARE ? next : null;
  }

  /** 提案された文字種を足す。setChecked がプールを引き直す */
  acceptSuggestion() {
    const next = this.pendingSuggestion;
    if (!next) return;
    this.pendingSuggestion = null;
    this.#suggestionDone = true;
    this.setChecked(next.key, true);
  }

  /** 提案を見送って次の問題へ。そのセッションでは二度と出さない */
  dismissSuggestion() {
    if (!this.pendingSuggestion) return;
    this.pendingSuggestion = null;
    this.#suggestionDone = true;
    this.#answerLocked = false;
    this.#nextQuestion();
    this.#host.requestUpdate();
  }

  /** 不正解の説明を読み終えて次の問題へ進む */
  continueToNext() {
    if (!this.awaitingContinue) return;
    this.awaitingContinue = false;
    this.showResult = false;
    this.resultCorrect = null;
    this.lastChoice = null;
    this.milestone = null;
    if (this.#takeGoalCard()) {
      // 選択肢は出さないのでロックは掛けたまま
      this.#host.requestUpdate();
      return;
    }
    this.#answerLocked = false;
    this.#nextQuestion();
    this.#host.requestUpdate();
  }

  /**
   * 目標に届いた回答の保留を消費して、区切りのカードを立てる。
   * 1 セッション・1 暦日に 1 回。同じ日に開き直しても最初の描画には出ない（初期高さを変えない）。
   * そのセッションで GOAL_CARD_MIN_ANSWERS 問答えるまでは保留のまま待つ
   */
  #takeGoalCard(): boolean {
    const today = this.#today();
    if (!this.#goalPending || this.#goalCardDay === today) return false;
    // 保留は待たせるので、出す瞬間に事実を見直す。日付が変わった・記録を消した・目標を上げた、
    // のどれかで届いていなければ、届いたと言わない（保留は捨てる。届く回答が改めて立てる）
    if (this.#goalPendingDay !== today || this.#store.todayCount < this.#store.goal) {
      this.#goalPending = false;
      return false;
    }
    if (this.#answeredThisSession < GOAL_CARD_MIN_ANSWERS) return false;
    this.#goalPending = false;
    this.#goalCardDay = today;
    this.#suggestBlockUntil = this.#answeredThisSession + GOAL_CARD_MIN_ANSWERS;
    // #serve が走らない窓（カード・提案）では向きも教示も無い。残すと前問の
    // 「この読み方の字を選んでください」が選択肢の無い画面に出る
    this.#clearOrientation();
    this.goalCard = {
      goal: this.#store.goal,
      today: this.#store.todayCount,
      correct: this.#store.todayCorrect,
      almost: this.#almostCount(),
      day: today,
    };
    return true;
  }

  /**
   * 区切り・休止の数字は「今日」のスナップショット。日をまたいで開いたままの画面で
   * 記録ラインが「今日 0 問」になったら、同じ箱に「今日 10 問」を出し続けない。
   * 毎描画で読まれる summary から呼ぶ（ストアの今日のカウンタも読み出しで日を繰り上げている）
   */
  #expireStaleCard() {
    const day = (this.goalCard ?? this.closedSummary)?.day;
    if (day !== undefined && day !== this.#today()) this.#dropGoalStates();
  }

  /** 向きと教示の印を落とす。出題ボックスが問題以外（カード・休止）を出す窓で呼ぶ */
  #clearOrientation() {
    this.reversed = false;
    this.firstContact = false;
    this.#endPreview();
  }

  /** 区切りと休止を畳んで出題に戻す（記録の消去・取り消しで数字が古くなったとき） */
  #dropGoalStates() {
    if (!this.goalCard && !this.sessionClosed) return;
    this.goalCard = null;
    this.sessionClosed = false;
    this.closedSummary = null;
    this.#answerLocked = false;
    this.#nextQuestion();
  }

  /**
   * 選択中の文字種のうち「今日覚えた字」= ほぼ定着で学習日がまだ今日だけの字数。
   * 日をあけてもう一度正解すると定着になる。2 日以上学んだ箱 4 や減衰した箱 5 は同じ日の
   * 速答でも定着に届くので数えない（「日をあけて」が嘘にならない範囲だけ言う）
   */
  #almostCount(): number {
    const today = this.#today();
    let n = 0;
    for (const c of this.#allSelected()) {
      const r = this.#store.rec(c.id);
      if (r && r[4] === 1 && r[1] === today && labelFor(r, today) === 'ほぼ定着') n++;
    }
    return n;
  }

  /** 区切りのカードから次の問題へ。休止からの再開も同じ */
  continueSession() {
    if (!this.goalCard && !this.sessionClosed) return;
    this.#dropGoalStates();
    this.#host.requestUpdate();
  }

  /**
   * 「今日はここまで」。出題を止めて、まとめの文だけを残す。
   * カードの数字は goalCard から休止の表示へ引き継ぐ。離脱の直前になるので記録を今書く
   */
  closeSession() {
    if (!this.goalCard) return;
    this.closedSummary = this.goalCard;
    this.goalCard = null;
    this.sessionClosed = true;
    this.currentCharacter = null;
    this.options = [];
    this.#clearOrientation();
    this.#store.flush();
    this.#host.requestUpdate();
  }

  setChecked(key: CategoryKey, value: boolean) {
    this[key] = value;
    this.#recomputeMax();
    this.resetQuiz();
    this.#saveSettings();
  }

  /** ドラッグ中の表示更新のみ。出題プールは引き直さない */
  setCharacterCount(count: number) {
    if (!Number.isFinite(count)) return;
    this.#desiredCount = Math.round(count);
    this.#host.requestUpdate();
  }

  /**
   * スライダーを離した時点でプールを引き直す。
   * 旧実装は input のたびに引き直していたので、ドラッグ 1 ティックごとに
   * 表示中の問題が捨てられて別の文字に切り替わっていた。
   */
  commitCharacterCount(count: number) {
    this.setCharacterCount(count);
    this.resetQuiz();
    this.#saveSettings();
  }

  toggleInstructions() {
    this.instructionsExpanded = !this.instructionsExpanded;
    this.#host.requestUpdate();
  }

  // -------------------------------------------------------------------------
  // 反応時間の計測に関わる合図。DOM の監視は要素側で行い、ここは結果だけ受け取る
  // -------------------------------------------------------------------------

  /** 今の問題を計測不能にする（タブが隠れた等）。次の問題には持ち越さない */
  markUntimed() {
    this.#questionUntimed = true;
  }

  /** true の間に出した問題は計測しない（ウェブフォントのロード待ち等） */
  setTimingBlocked(blocked: boolean) {
    this.#timingBlocked = blocked;
    if (blocked) this.#questionUntimed = true;
  }

  /** 保留中の記録を今すぐ書く（ページが隠れるときに要素側が呼ぶ） */
  flushProgress() {
    this.#store.flush();
  }

  // -------------------------------------------------------------------------
  // 学習の記録（読み取り）
  // -------------------------------------------------------------------------

  get #allCharacters(): Character[] {
    this.#allCache ??= [
      ...characters.vowels,
      ...characters.consonants,
      ...this.#dependentVowels,
      ...this.#virama,
      ...this.#ayogavaha,
      ...this.#conjuncts,
      ...characters.numerals,
    ];
    return this.#allCache;
  }

  #charactersOf(key: CategoryKey): Character[] {
    switch (key) {
      case 'vowelsChecked':
        return characters.vowels;
      case 'consonantsChecked':
        return characters.consonants;
      case 'dependentVowelsChecked':
        return this.#dependentVowels;
      case 'viramaChecked':
        return this.#virama;
      case 'ayogavahaChecked':
        return this.#ayogavaha;
      case 'conjunctsChecked':
        return this.#conjuncts;
      case 'numeralsChecked':
        return characters.numerals;
    }
  }

  /** 1 文字の状態ラベル */
  labelOf(c: Character): Label {
    return labelFor(this.#store.rec(c.id), this.#today());
  }

  /** 1 文字の実効箱（減衰適用後）。未学習は 0 */
  boxOf(c: Character): number {
    const r = this.#store.rec(c.id);
    return r ? effBox(r, this.#today()) : 0;
  }

  /** 出題ボックスの上の 1 行と定着バーの元になる数字。分母は選択中の文字種の合計 */
  get summary(): ProgressSummary {
    this.#expireStaleCard();
    const today = this.#today();
    let mastered = 0;
    let advanced = 0;
    let encountered = 0;
    for (const c of this.#allSelected()) {
      const r = this.#store.rec(c.id);
      if (!r) continue;
      encountered++;
      if (effBox(r, today) >= 3) advanced++;
      if (labelFor(r, today) === '定着') mastered++;
    }
    // 分母は出題済みの字。まだ何も答えていない間は今の出題セットの字数を床にする
    // （初回に「定着 0 / 0」と出さない）。何も選んでいなければ 0
    const floor = Math.min(this.characterCount, this.maxCharacters);
    return {
      today: this.#store.todayCount,
      goal: this.#store.goal,
      mastered,
      advanced,
      total: Math.max(encountered, floor),
    };
  }

  /** 文字種ごとの内訳（7 行）。選択の有無によらず全部出す */
  get categoryBreakdown(): CategoryBreakdown[] {
    return CATEGORIES.map((cat) => {
      const row = {...cat, mastered: 0, learning: 0, ambiguous: 0, unseen: 0, total: 0};
      for (const c of this.#charactersOf(cat.key)) {
        row.total++;
        const label = this.labelOf(c);
        if (label === '未学習') row.unseen++;
        else if (label === '定着') row.mastered++;
        else if (label === '苦手' || label === 'あいまい') row.ambiguous++;
        else row.learning++;
      }
      return row;
    });
  }

  /**
   * まだ曖昧な字（実効箱 ≤ 2）を、苦手な順に最大 10 件。
   * 同値のときは誤答率、さらに id で決める（毎回並びが変わらないように）。
   */
  get weakCharacters(): {c: Character; label: Label}[] {
    const today = this.#today();
    return this.#allCharacters
      .map((c) => ({c, r: this.#store.rec(c.id)}))
      .filter((x): x is {c: Character; r: NonNullable<typeof x.r>} => x.r !== undefined)
      .filter((x) => effBox(x.r, today) <= 2)
      .sort(
        (a, b) =>
          effBox(a.r, today) - effBox(b.r, today) ||
          b.r[3] / b.r[2] - a.r[3] / a.r[2] ||
          (a.c.id < b.c.id ? -1 : a.c.id > b.c.id ? 1 : 0)
      )
      .slice(0, WEAK_LIST)
      .map(({c, r}) => ({c, label: labelFor(r, today)}));
  }

  /** 累計。分母は現行データの字だけ（データから消えた id は数えない） */
  get totals(): {answered: number; correct: number; days: number; mastered: number} {
    const today = this.#today();
    let answered = 0;
    let correct = 0;
    let mastered = 0;
    for (const c of this.#allCharacters) {
      const r = this.#store.rec(c.id);
      if (!r) continue;
      answered += r[2];
      correct += r[2] - r[3];
      if (labelFor(r, today) === '定着') mastered++;
    }
    return {answered, correct, days: this.#store.dayCount, mastered};
  }

  /** 直近 7 日（古い順）に学習したか。数字も「途切れた」も出さない点列の元 */
  get recentDays(): boolean[] {
    const today = this.#today();
    const days = new Set(this.#store.days);
    return Array.from({length: 7}, (_, i) => days.has(today - 6 + i));
  }

  get goal(): Goal {
    return this.#store.goal;
  }

  /** この環境で記録を保存できる */
  get recordingAvailable(): boolean {
    return this.#store.available;
  }

  /** 「このブラウザに保存する」の状態 */
  get recordingEnabled(): boolean {
    return this.#store.enabled;
  }

  /** 未知の新しい版の記録を読んだので、このビルドでは読みも書きもしない */
  get recordingReadOnly(): boolean {
    return this.#store.readOnly;
  }

  /** 別のタブが書いた記録を取り込む（要素側が storage イベントで呼ぶ） */
  resyncProgress() {
    this.#store.resync();
    this.#host.requestUpdate();
  }

  /** 記録が 1 件も無い（消去の確認文などに使う） */
  get progressIsEmpty(): boolean {
    return this.#store.isEmpty;
  }

  // -------------------------------------------------------------------------
  // 学習の記録（操作）
  // -------------------------------------------------------------------------

  toggleProgressPanel() {
    this.progressPanelOpen = !this.progressPanelOpen;
    if (!this.progressPanelOpen) this.cancelClearProgress();
    this.#host.requestUpdate();
  }

  /**
   * 読み方から字を選ぶ向きを混ぜるか（学習の記録パネル）。文字種と同じく即時に保存する。
   * 切ったときに反転の問題が出ていれば、字は変えずにその問題を通常の向きへ戻す
   * （切る人は今その向きに困っている）。採点は出題時の向き（#servedReversed）で決まるので、
   * 戻した問題は反転のまま扱う: 読みを問題文として見せたあとで答えが露出しているため、
   * 正解で記録を動かさず、誤答で箱を落とさない
   */
  setReverseEnabled(enabled: boolean) {
    if (this.reverseEnabled === enabled) return;
    this.reverseEnabled = enabled;
    this.#saveSettings();
    if (!enabled && this.reversed) this.reversed = false;
    this.#host.requestUpdate();
  }

  setGoal(goal: Goal) {
    this.#store.setGoal(goal);
    // 区切り・休止のまとめは目標のスナップショットなので畳む。休止中に目標を上げるのは
    // 「やはり続ける」の合図でもある
    this.#dropGoalStates();
    this.#host.requestUpdate();
  }

  setRecordingEnabled(enabled: boolean) {
    this.#store.setEnabled(enabled);
    this.#host.requestUpdate();
  }

  /** 消去の 1 段目。8 秒操作が無ければ元に戻る */
  requestClearProgress() {
    this.confirmingClear = true;
    clearTimeout(this.#confirmTimer);
    this.#confirmTimer = setTimeout(() => {
      this.#confirmTimer = undefined;
      this.confirmingClear = false;
      this.#host.requestUpdate();
    }, CONFIRM_MS);
    this.#host.requestUpdate();
  }

  cancelClearProgress() {
    clearTimeout(this.#confirmTimer);
    this.#confirmTimer = undefined;
    if (!this.confirmingClear) return;
    this.confirmingClear = false;
    this.#host.requestUpdate();
  }

  /** 消去の 2 段目。10 秒間だけ「元に戻す」が効く */
  confirmClearProgress() {
    if (!this.confirmingClear) return;
    this.cancelClearProgress();
    const {count, persisted} = this.#store.clear();
    this.clearedCount = count;
    this.clearedPersisted = persisted;
    // 記録に付随する帳簿も捨てる。プールは引き直さない（消去は学習の中断ではない）
    this.#hold.clear();
    this.#boost.clear();
    this.#recent = [];
    this.#recentBoxes = [];
    this.streak = 0;
    this.milestone = null;
    // 区切り・休止の数字はスナップショットなので、記録が変わったら畳む（その日は再表示しない）
    this.#dropGoalStates();
    clearTimeout(this.#undoTimer);
    this.#undoTimer = setTimeout(() => {
      this.#undoTimer = undefined;
      this.clearedCount = null;
      this.#store.forgetUndo();
      this.#host.requestUpdate();
    }, UNDO_MS);
    this.#host.requestUpdate();
  }

  undoClearProgress() {
    clearTimeout(this.#undoTimer);
    this.#undoTimer = undefined;
    this.clearedCount = null;
    this.#store.undoClear();
    this.#dropGoalStates();
    this.#host.requestUpdate();
  }
}
